import { PlusOutlined } from '@ant-design/icons';
import { Button, Space } from 'antd';
import { runInAction } from 'mobx';
import { observer, useLocalStore } from 'mobx-react-lite';
import * as React from 'react';
import MLModel from '../../../ml_models/models/MLModel';
import { MLService } from '../../../ml_models/services';
import PromptTemplateService from '../../../prompt_templates/services/PromptTemplatesService';
import { PromptTemplate } from '../../../prompt_templates/types';
import { ApplicationDefinitionsService } from '../../../iota_applications/services';
import { ApplicationDefinitionBase } from '../../../iota_applications/types/ApplicationDefinition';
import ScriptManagementService from '../../../script_management/services/ScriptManagementService';
import { Script } from '../../../script_management/types';
import CustomBlock, {
    CustomBlockType,
    hasPipeline,
    PipelineStep,
    PipelineStepFieldValue,
    PipelineStepType
} from './CustomBlock';
import './CustomBlockConfigurator.less';

interface Props {
    value?: string;
    onChange?: (value: string) => void;
    projectId: string;
    disabled?: boolean;
}

export const createPipelineStep = <TType extends PipelineStepType>(
    type: TType
): Extract<PipelineStep, { type: TType }> => {
    const typeMap: Record<PipelineStepType, PipelineStep> = {
        remote: { type: 'remote', url: '' },
        image: { type: 'image', model_id: '' },
        text: { type: 'text', model_id: '' },
        client_taxonomy: {
            type: 'client_taxonomy',
            schema_version: 1,
            application_id: '',
            reference_data: {
                collections: [
                    {
                        data_set: 'business_classification',
                        script_id: ''
                    }
                ]
            },
            use_cached_data: true,
            refresh_period: 'PT1H'
        }
    };

    return typeMap[type] as Extract<PipelineStep, { type: TType }>;
};

const CustomBlockConfigurator: React.FC<Props> = ({ value, onChange, projectId, disabled = false }) => {
    const blocks = React.useMemo(() => {
        if (!value) {
            return [];
        }
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) {
                return parsed as CustomBlockType[];
            }
            return [];
        } catch (error) {
            console.error('Failed to parse custom blocks JSON:', error);
            return [];
        }
    }, [value]);

    const store = useLocalStore(() => ({
        mlModels: [] as MLModel[],
        promptTemplates: [] as PromptTemplate[],
        applicationDefinitions: [] as ApplicationDefinitionBase[],
        scripts: [] as Script[],
        isLoadingModels: false,
        isLoadingPrompts: false,
        isLoadingApplications: false,
        isLoadingScripts: false,
        mlService: new MLService(),
        promptService: new PromptTemplateService(),
        applicationDefinitionsService: new ApplicationDefinitionsService(),
        scriptManagementService: new ScriptManagementService(),

        async loadMlModels() {
            try {
                runInAction(() => {
                    this.isLoadingModels = true;
                });

                const models = await this.mlService.getMLModelsForProject(projectId);

                runInAction(() => {
                    this.mlModels = models;
                });
            } catch (error) {
                console.error('Failed to load ML models:', error);
            } finally {
                runInAction(() => {
                    this.isLoadingModels = false;
                });
            }
        },

        async loadPromptTemplates() {
            try {
                runInAction(() => {
                    this.isLoadingPrompts = true;
                });

                const templates = await this.promptService.getPromptTemplates(projectId);

                runInAction(() => {
                    this.promptTemplates = templates;
                });
            } catch (error) {
                console.error('Failed to load prompt templates:', error);
            } finally {
                runInAction(() => {
                    this.isLoadingPrompts = false;
                });
            }
        },

        async loadApplicationDefinitions() {
            try {
                runInAction(() => {
                    this.isLoadingApplications = true;
                });

                const applicationDefinitions =
                    await this.applicationDefinitionsService.getApplicationDefinitionsForProject(projectId);

                runInAction(() => {
                    this.applicationDefinitions = applicationDefinitions;
                });
            } catch (error) {
                console.error('Failed to load application definitions:', error);
            } finally {
                runInAction(() => {
                    this.isLoadingApplications = false;
                });
            }
        },

        async loadScripts() {
            try {
                runInAction(() => {
                    this.isLoadingScripts = true;
                });

                const scripts = await this.scriptManagementService.getScripts();

                runInAction(() => {
                    this.scripts = scripts;
                });
            } catch (error) {
                console.error('Failed to load scripts:', error);
            } finally {
                runInAction(() => {
                    this.isLoadingScripts = false;
                });
            }
        }
    }));

    React.useEffect(() => {
        store.loadMlModels();
        store.loadPromptTemplates();
        store.loadApplicationDefinitions();
        store.loadScripts();
    }, [projectId, store]);

    const handleAddBlock = () => {
        const newBlock: CustomBlockType = {
            type: 'INTERPRETATION_BLOCK',
            pipeline: []
        };
        const updatedValue = [...blocks, newBlock];
        onChange?.(JSON.stringify(updatedValue));
    };

    const handleRemoveBlock = (index: number) => {
        const updatedValue = blocks.filter((_, i) => i !== index);
        onChange?.(JSON.stringify(updatedValue));
    };

    const handleBlockTypeChange = (index: number, newType: 'SMART_BLOCK' | 'INTERPRETATION_BLOCK') => {
        const updatedValue = [...blocks];
        if (newType === 'SMART_BLOCK') {
            updatedValue[index] = {
                type: 'SMART_BLOCK',
                model_id: ''
            };
        } else {
            updatedValue[index] = {
                type: 'INTERPRETATION_BLOCK',
                pipeline: []
            };
        }
        onChange?.(JSON.stringify(updatedValue));
    };

    const handleSmartBlockModelChange = (index: number, modelId: string) => {
        const updatedValue = [...blocks];
        const block = updatedValue[index];

        if (!block || block.type !== 'SMART_BLOCK') {
            return;
        }

        updatedValue[index] = {
            ...block,
            model_id: modelId
        };
        onChange?.(JSON.stringify(updatedValue));
    };

    const handleAddPipelineStep = (blockIndex: number) => {
        const updatedValue = [...blocks];
        const block = updatedValue[blockIndex];

        if (!hasPipeline(block)) {
            return;
        }

        const newStep: PipelineStep = {
            type: 'image',
            model_id: ''
        };

        updatedValue[blockIndex] = {
            ...block,
            pipeline: [...block.pipeline, newStep]
        } as CustomBlockType;

        onChange?.(JSON.stringify(updatedValue));
    };

    const handleRemovePipelineStep = (blockIndex: number, stepIndex: number) => {
        const updatedValue = [...blocks];
        const block = updatedValue[blockIndex];

        if (!hasPipeline(block)) {
            return;
        }

        updatedValue[blockIndex] = {
            ...block,
            pipeline: block.pipeline.filter((_, i) => i !== stepIndex)
        } as CustomBlockType;
        onChange?.(JSON.stringify(updatedValue));
    };

    const handlePipelineStepTypeChange = (blockIndex: number, stepIndex: number, newType: PipelineStepType) => {
        const updatedValue = [...blocks];
        const block = updatedValue[blockIndex];

        if (!hasPipeline(block)) {
            return;
        }

        const step = createPipelineStep(newType);
        if (!step) {
            return;
        }

        updatedValue[blockIndex] = {
            ...block,
            pipeline: block.pipeline.map((s, i) => (i === stepIndex ? step : s))
        } as CustomBlockType;

        onChange?.(JSON.stringify(updatedValue));
    };

    const handlePipelineStepFieldChange = (
        blockIndex: number,
        stepIndex: number,
        field: string,
        fieldValue: PipelineStepFieldValue
    ) => {
        const updatedValue = [...blocks];
        const block = updatedValue[blockIndex];

        if (!hasPipeline(block)) {
            return;
        }

        const currentStep = block.pipeline[stepIndex];
        let step = { ...currentStep } as PipelineStep;

        if (fieldValue === undefined || fieldValue === '') {
            if (field in step) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                delete (step as any)[field];
            }
        } else {
            step = { ...currentStep, [field]: fieldValue } as PipelineStep;
        }

        updatedValue[blockIndex] = {
            ...block,
            pipeline: block.pipeline.map((s, i) => (i === stepIndex ? step : s))
        } as CustomBlockType;

        onChange?.(JSON.stringify(updatedValue));
    };

    const handleMovePipelineStep = (blockIndex: number, dragIndex: number, hoverIndex: number) => {
        const updatedValue = [...blocks];
        const block = updatedValue[blockIndex];

        if (!hasPipeline(block)) {
            return;
        }

        const newPipeline = [...block.pipeline];
        const [draggedStep] = newPipeline.splice(dragIndex, 1);
        newPipeline.splice(hoverIndex, 0, draggedStep);

        updatedValue[blockIndex] = {
            ...block,
            pipeline: newPipeline
        } as CustomBlockType;

        onChange?.(JSON.stringify(updatedValue));
    };

    return (
        <div className="custom-block-configurator">
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {blocks.map((block, index) => (
                    <CustomBlock
                        key={index}
                        block={block}
                        index={index}
                        disabled={disabled}
                        store={store}
                        onRemoveBlock={handleRemoveBlock}
                        onBlockTypeChange={handleBlockTypeChange}
                        onSmartBlockModelChange={handleSmartBlockModelChange}
                        onAddPipelineStep={handleAddPipelineStep}
                        onRemovePipelineStep={handleRemovePipelineStep}
                        onPipelineStepTypeChange={handlePipelineStepTypeChange}
                        onPipelineStepFieldChange={handlePipelineStepFieldChange}
                        onMovePipelineStep={handleMovePipelineStep}
                    />
                ))}

                <Button onClick={handleAddBlock} icon={<PlusOutlined />} disabled={disabled} block>
                    Add Block
                </Button>
            </Space>
        </div>
    );
};

export default observer(CustomBlockConfigurator);
